import apis from './apis';

/**
 * Document Intelligence (Living Documents) - the consolidated "is this document
 * still aligned with its sources, and what can I re-run" read for a draft.
 *
 * Powers the intelligence status badge and the insights panel. Read-only.
 */

export type IntelligenceStatus =
  | 'aligned' | 'behind' | 'not_generated' | 'unlinked' | 'unknown';

export interface IntelligenceSignal {
  kind: 'template' | 'branding' | 'playbook';
  linked?: boolean;
  status: 'aligned' | 'behind' | 'unversioned' | 'unknown';
  from_version?: number | null;
  to_version?: number | null;
  /** Present on a playbook signal - the review to re-run. */
  review_session_uuid?: string;
  /** A runnable action key when the signal is behind. */
  action?: 'rerun_from_source' | 'reapply_branding' | 'rerun_review' | null;
}

export interface DraftIntelligence {
  status: IntelligenceStatus;
  headline: string;
  signals: IntelligenceSignal[];
}

export async function getDraftIntelligence(draftUuid: string): Promise<DraftIntelligence> {
  const res = await apis.get(`/drafting/drafts/${draftUuid}/intelligence`);
  return (res.data?.data ?? res.data) as DraftIntelligence;
}

export interface IntelligenceSummary {
  status: IntelligenceStatus;
  behind_kinds: string[];
}

/** Compact status for many drafts at once - powers the list column badge. */
export async function getDraftIntelligenceBatch(
  draftUuids: string[],
): Promise<Record<string, IntelligenceSummary>> {
  if (!draftUuids.length) return {};
  const res = await apis.post('/drafting/drafts/intelligence-batch', { draft_uuids: draftUuids });
  const data = res.data?.data ?? res.data;
  return (data?.statuses ?? {}) as Record<string, IntelligenceSummary>;
}

export interface RerunOutdatedResult {
  queued: number;
  skipped: number;
  drafts: string[];
}

/** Refresh every template-behind draft in one action (optionally scoped). */
export async function rerunOutdatedDrafts(draftUuids?: string[]): Promise<RerunOutdatedResult> {
  const body = draftUuids && draftUuids.length ? { draft_uuids: draftUuids } : {};
  const res = await apis.post('/drafting/drafts/rerun-outdated', body);
  return (res.data?.data ?? res.data) as RerunOutdatedResult;
}
